import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { compose } from 'recompose';
import { Scene, Camera, Object3D } from 'three';
import { TimelineMax, TweenMax } from 'gsap';
import Hammer from 'hammerjs';
import { Instance as Hamster } from 'hamsterjs';
import createDebug from 'debug';

import withUrlGenerator from '../../lib/withUrlGenerator';
import withTranslations from '../../lib/withTranslations';
import Stars from '../../lib/Stars';
import Light from '../../lib/LightCanvas';
import Map from '../../lib/Map';
import ClockAudio from '../../lib/ClockAudio';
import Title from '../partials/Title';
import Loading from '../partials/Loading';
import Stats from '../partials/Stats';
import HumAlong from '../partials/HumAlong';

import createStarsFadeIn from '../../animations/starsFadeIn';
import createSlideContentAnimation from '../../animations/slideContent';
import createMapFadeInAnimation from '../../animations/mapFadeIn';

const debug = createDebug('app:home');

const propTypes = {
    t: PropTypes.func.isRequired,
    statsCount: PropTypes.number.isRequired,
    statsCountries: PropTypes.object.isRequired,
    width: PropTypes.number,
    height: PropTypes.number,
    scene: PropTypes.instanceOf(Scene),
    camera: PropTypes.instanceOf(Camera),
    audio: PropTypes.instanceOf(ClockAudio),
    percent: PropTypes.number,
    mouseWheelTimeout: PropTypes.number,
};

const defaultProps = {
    width: null,
    height: null,
    scene: null,
    camera: null,
    audio: null,
    percent: 0,
    mouseWheelTimeout: 200,
};

class HomePage extends Component {
    constructor(props) {
        super(props);

        this.onTick = this.onTick.bind(this);
        this.onClickTitle = this.onClickTitle.bind(this);
        this.onSwipeUp = this.onSwipeUp.bind(this);
        this.onSwipeDown = this.onSwipeDown.bind(this);
        this.onPan = this.onPan.bind(this);
        this.onPanStart = this.onPanStart.bind(this);
        this.onPanEnd = this.onPanEnd.bind(this);
        this.onMouseWheel = this.onMouseWheel.bind(this);
        this.onClickArrowUp = this.onClickArrowUp.bind(this);
        this.onClickArrowDown = this.onClickArrowDown.bind(this);
        this.onStatsConnected = this.onStatsConnected.bind(this);
        this.onStatsCountChange = this.onStatsCountChange.bind(this);
        this.onStatsCountStart = this.onStatsCountStart.bind(this);
        this.onStatsClickCount = this.onStatsClickCount.bind(this);
        this.onLoadingComplete = this.onLoadingComplete.bind(this);
        this.onLoadingLastProgress = this.onLoadingLastProgress.bind(this);

        this.timeline = null;
        this.timelineHum = null;
        this.timelineSlides = null;
        this.mapContainer = null;
        this.map = null;
        this.stars = null;
        this.light = null;
        this.hammer = null;
        this.lastDeltaY = null;
        this.mousewheelTimeout = null;
        this.panning = false;

        this.refTitle = null;
        this.refLoading = null;
        this.refHumAlong = null;
        this.refContent = null;
        this.refSlideCount = null;
        this.refArrowUp = null;
        this.refArrowDown = null;

        this.state = {
            ready: false,
            loadingVisible: false,
            mapVisible: false,
            statsConnected: false,
            updateAudioCount: false,
            count: -1,
        };
    }

    componentDidMount() {
        const { width, height, camera } = this.props;

        // Scenes
        const sceneOptions = {
            camera,
            width,
            height,
        };
        this.mapContainer = new Object3D();
        this.map = new Map(sceneOptions);
        this.light = new Light(sceneOptions);
        this.stars = new Stars(sceneOptions);

        if (this.props.scene) {
            this.createScene(this.props.scene);
            this.resizeScene(this.props.scene, this.props.width, this.props.height);
        }

        // Timelines
        this.timeline = this.createTimeline().paused(false);
        this.timelineSlides = this.createSlidesTimeline();
        this.timelineHum = this.refHumAlong.getTimeline();

        // Gestures
        this.hammer = new Hammer(this.refContent);
        this.hammer.get('swipe').set({
            direction: Hammer.DIRECTION_VERTICAL,
        });
        this.hammer.get('pan').set({
            direction: Hammer.DIRECTION_VERTICAL,
        });
        this.hammer.on('swipeup', this.onSwipeUp);
        this.hammer.on('swipedown', this.onSwipeDown);
        this.hammer.on('pan', this.onPan);
        this.hammer.on('panstart', this.onPanStart);
        this.hammer.on('panend', this.onPanEnd);

        this.hamster = new Hamster(this.refContent);
        this.hamster.wheel(this.onMouseWheel);

        // Ticker
        TweenMax.ticker.addEventListener('tick', this.onTick);

        gaTrackPage('home');
    }

    componentWillReceiveProps(nextProps) {
        const countChanged = nextProps.statsCount !== this.props.statsCount;
        if (countChanged && this.state.statsConnected) {
            this.setState({
                count: nextProps.statsCount,
            });
        }
    }

    componentDidUpdate(prevProps, prevState) {
        const readyChanged = prevState.ready !== this.state.ready;
        if (readyChanged && this.state.ready) {
            if (this.timeline.paused()) {
                this.timeline.play('count');
            }
        }

        // Build scene
        const sceneChanged = prevProps.scene !== this.props.scene;
        if (sceneChanged) {
            if (prevProps.scene !== null) {
                this.destroyScene(prevProps.scene);
            }
            this.createScene(this.props.scene);
            this.resizeScene(this.props.scene, this.props.width, this.props.height);
        }

        // Resize scene
        const sizeChanged = (
            prevProps.width !== this.props.width ||
            prevProps.height !== this.props.height
        );
        if (sizeChanged && this.props.scene) {
            this.resizeScene(this.props.scene, this.props.width, this.props.height);
        }

        // Update countries
        const mapVisibleChanged = (
            prevState.mapVisible !== this.state.mapVisible
        );
        const statsCountriesChanged = (
            prevProps.statsCountries !== this.props.statsCountries
        );
        if ((mapVisibleChanged || statsCountriesChanged) && this.state.mapVisible) {
            this.updateMapCountries(this.props.statsCountries);
        }

        const updateAudioChanged = prevState.updateAudioCount !== this.state.updateAudioCount;
        const countChanged = prevProps.statsCount !== this.props.statsCount;
        if ((countChanged || updateAudioChanged) && this.state.updateAudioCount) {
            this.props.audio.setCount(this.props.statsCount);
        }
    }

    componentWillUnmount() {
        if (this.props.scene !== null) {
            this.destroyScene(this.props.scene);
        }

        if (this.timeline !== null) {
            this.timeline.kill();
            this.timeline = null;
        }

        TweenMax.ticker.removeEventListener('tick', this.onTick);

        if (this.hammer !== null) {
            this.hammer.destroy();
        }

        if (this.hammer !== null) {
            this.hamster.unwheel(this.onMouseWheel);
        }

        if (this.fakeInterval !== null) {
            clearInterval(this.fakeInterval);
        }
    }

    onTick() {
        this.tick();
    }

    onClickTitle() {
        this.timeline.play('loading');

        gaTrackEvent('Navigation', 'click_title');
        gaTrackPage('count');
    }

    onLoadingComplete() {
        this.setState({
            ready: true,
        });
    }

    onLoadingLastProgress(progress) {
        if (!this.props.audio.playing) {
            this.props.audio.play();
        }
        // this.props.audio.volume(progress);
    }

    onSwipeUp() {
        this.slideDown();

        gaTrackEvent('Navigation', 'swipe_up');
    }

    onSwipeDown() {
        this.slideUp();

        gaTrackEvent('Navigation', 'swipe_down');
    }

    onClickArrowDown() {
        this.slideDown();

        gaTrackEvent('Navigation', 'click_arrow_down');
    }

    onClickArrowUp() {
        this.slideUp();

        gaTrackEvent('Navigation', 'click_arrow_up');
    }

    onStatsConnected() {
        debug('Stats connected.');
        this.setState({
            statsConnected: true,
        });
    }

    onStatsCountChange(count, delta) {
        this.stars.addSparks(delta / 10);
    }

    onStatsCountStart(count) {
        if (this.props.audio.count !== count) {
            this.props.audio.setCount(count);
        }
    }

    onStatsClickCount() {
        const zoomMapTime = this.timeline.getLabelTime('zoom_map');
        if (this.timeline.time() > zoomMapTime) {
            const tween = this.timeline.tweenTo(zoomMapTime);
            this.updateMapCountries({});
            tween.eventCallback('onComplete', () => {
                this.setState(() => ({
                    mapVisible: false,
                }));
            });
        } else {
            this.timeline.play('zoom_map');
            this.setState(() => ({
                mapVisible: true,
            }));
        }

        gaTrackEvent('Navigation', 'click_stats');
    }

    onMouseWheel(event, delta, deltaX, deltaY) {
        const { mouseWheelTimeout } = this.props;

        if (this.mousewheelTimeout) {
            clearTimeout(this.mousewheelTimeout);
        }

        if (this.panning) {
            return;
        }

        const { height } = this.props;
        const distance = this.lastDeltaY !== null ? deltaY + this.lastDeltaY : 0;
        const currentProgress = -(distance / height);
        const progress = Math.min(
            Math.max(0, this.timelineSlides.panProgress + currentProgress),
            1,
        );
        this.pan(progress);
        this.lastDeltaY = deltaY;

        this.mousewheelTimeout = setTimeout(() => {
            this.mousewheelTimeout = null;
            this.onPanEnd();
        }, mouseWheelTimeout);
    }

    onPanStart() {
        this.panning = true;
        this.timelineSlides.panStartProgress = this.timelineSlides.progress();

        if (this.mousewheelTimeout) {
            clearTimeout(this.mousewheelTimeout);
        }
    }

    onPan(e) {
        const { height } = this.props;
        const distance = this.lastDeltaY !== null ? e.deltaY - this.lastDeltaY : 0;
        const currentProgress = -(distance / height);
        const progress = Math.min(
            Math.max(0, this.timelineSlides.panProgress + currentProgress),
            1,
        );
        this.pan(progress);
        this.lastDeltaY = e.deltaY;
    }

    onPanEnd() {
        this.panning = false;
        this.lastDeltaY = null;
        const { panProgress, panStartProgress } = this.timelineSlides;
        const direction = panProgress > panStartProgress ? 'up' : 'down';
        const panThresold = 0.3;
        if (
            (
                direction === 'up' && panProgress > panThresold
            ) || (
                direction === 'down' && panProgress > (1 - panThresold)
            )
        ) {
            this.slideDown();
        } else {
            this.slideUp();
        }
    }

    pan(progress) {
        const time = this.timelineSlides.duration() * progress;
        this.timelineSlides.tweenTo(time);
        this.timelineSlides.panProgress = progress;
    }

    slideUp() {
        const tween = this.timelineSlides.tweenTo(0);
        tween.eventCallback('onStart', () => {
            this.timelineHum.paused(true);
        });
        tween.eventCallback('onComplete', () => {
            this.timelineHum.progress(0);
        });
    }

    slideDown() {
        const tween = this.timelineSlides.tweenTo(this.timelineSlides.duration());
        tween.eventCallback('onComplete', () => {
            // if (this.timelineHum.progress() < 1) {
            //     this.timelineHum.paused(false);
            // }
        });
    }

    createTimeline() {
        const timeline = new TimelineMax({
            paused: true,
            onComplete: () => {
                gaTrackPage('map');
            },
        });

        // Title
        timeline.add(createStarsFadeIn(this.stars));
        timeline.add(this.refTitle.getTimelineFadeIn().paused(false));

        // Wait for tap
        timeline.addPause();
        timeline.addLabel('loading');

        // Loading
        timeline.add(this.refTitle.getTimelineFadeOut().paused(false));
        timeline.add(this.refLoading.getTimelineFadeIn().paused(false));

        // Wait for progress
        timeline.addPause('+=0', () => {
            this.setState(() => ({ loadingVisible: true }));
        });
        timeline.addLabel('count');

        // Content
        timeline.add(this.refLoading.getTimelineFadeOut().paused(false), '+=1');

        const mapFadeInTimeline = createMapFadeInAnimation(this.map);
        const contentTimeline = new TimelineMax();
        contentTimeline.add(this.refStats.getTimelineFadeIn().paused(false), 0);
        contentTimeline.add(this.stars.getFadeOutTimeline(), 0);
        contentTimeline.addCallback(() => {
            this.stars.addSparks(1);
        }, 2);
        contentTimeline.add(mapFadeInTimeline, '-=2');
        contentTimeline.fromTo(this.stars, 2, {
            alpha: 1,
        }, {
            alpha: 0,
        }, '-=2');
        contentTimeline.fromTo(this.refArrowDown, 1, {
            autoAlpha: 0,
        }, {
            autoAlpha: 1,
        });

        timeline.add(contentTimeline);

        // Zoom map
        timeline.addPause('+=0', () => {
            this.setState(() => ({ updateAudioCount: true }));
        });
        timeline.addLabel('zoom_map');

        const zoomMapTimeline = new TimelineMax();
        zoomMapTimeline.fromTo(this.map, 2, {
            mapScale: mapFadeInTimeline.finalScale,
        }, {
            mapScale: 1,
        }, 0);

        zoomMapTimeline.add(this.refStats.getTimelineFadeOut().paused(false), 0);

        timeline.add(zoomMapTimeline);

        return timeline;
    }

    createSlidesTimeline() {
        const duration = 2;
        const timeline = new TimelineMax({
            paused: true,
            onComplete: () => {
                gaTrackPage('hum');
            },
        });
        timeline.panStartProgress = 0;
        timeline.panProgress = 0;

        timeline.add(createSlideContentAnimation({
            arrowDown: this.refArrowDown,
            arrowUp: this.refArrowUp,
            slideTop: this.refStats.getContainerRef(),
            slideBottom: this.refHumAlong.getContainerRef(),
            map: this.map,
            halo: this.light,
            stars: this.stars,
            duration,
        }));

        return timeline;
    }

    updateMapCountries(countries) {
        if (this.map === null) {
            return;
        }
        const max = Object.keys(countries).reduce((maxCount, code) => (
            Math.max(maxCount, countries[code])
        ), 0);
        const countriesProgress = Object.keys(countries).reduce((items, code) => ({
            ...items,
            [code]: countries[code] / max,
        }), {});
        this.map.setVisibleCountries(countriesProgress);
    }

    createScene(scene) {
        const { camera } = this.props;

        // Map
        this.map.camera = camera;
        this.map.position.z = -200;
        this.map.visible = false;
        this.map.build();
        this.mapContainer.add(this.map);
        scene.add(this.mapContainer);

        // Stars
        this.stars.camera = camera;
        this.stars.position.z = -200;
        this.stars.visible = false;
        this.stars.build();
        scene.add(this.stars);

        // Light
        this.light.camera = camera;
        this.light.visible = false;
        this.light.build();
        scene.add(this.light);
    }

    resizeScene(scene, width, height) {
        this.light.setSize(width, height);
        this.map.setSize(width, height);
    }

    destroyScene(scene) {
        if (this.mapContainer !== null) {
            scene.remove(this.mapContainer);
        }
        if (this.stars !== null) {
            scene.remove(this.stars);
        }
        if (this.light !== null) {
            scene.remove(this.light);
        }
    }

    tick() {
        const { audio } = this.props;
        if (this.light !== null && audio !== null && this.light.visible) {
            this.light.setIntensity(audio.getIntensity());
        }

        if (this.stars !== null && this.stars.visible) {
            this.stars.tick();
        }

        if (this.refHumAlong !== null) {
            this.refHumAlong.tick();
        }
    }

    render() {
        const { t, statsCount, percent } = this.props;
        const { count, loadingVisible, updateAudioCount } = this.state;
        return (
            <div className="page page-home">
                <Title
                    title={t('title')}
                    tapToReveal={t('tap_to_reveal')}
                    onClick={this.onClickTitle}
                    ref={(ref) => { this.refTitle = ref; }}
                />
                <Loading
                    turnOnSound={t('turnon_sound')}
                    percent={percent}
                    onComplete={this.onLoadingComplete}
                    onLastProgress={this.onLoadingLastProgress}
                    visible={loadingVisible}
                    ref={(ref) => { this.refLoading = ref; }}
                />
                <div
                    className="slide slide-content fullscreen"
                    ref={(ref) => { this.refContent = ref; }}
                >
                    <Stats
                        count={count}
                        realCount={statsCount}
                        nowListening={t('now_listening')}
                        connecting={t('connecting')}
                        aroundTheWorld={t('around_the_world')}
                        onConnected={this.onStatsConnected}
                        onCountStart={this.onStatsCountStart}
                        onCountChange={this.onStatsCountChange}
                        onClickCount={this.onStatsClickCount}
                        ref={(ref) => { this.refStats = ref; }}
                    />
                    <HumAlong
                        humReady={t('hum_along_ready')}
                        humSteps={[
                            t('hum_along_step1'),
                            t('hum_along_step2'),
                            t('hum_along_step3'),
                            t('hum_along_step4'),
                            t('hum_along_step5'),
                            t('hum_along_step6'),
                        ]}
                        humEnd={t('hum_along_end')}
                        ref={(ref) => { this.refHumAlong = ref; }}
                    />
                </div>
                <button
                    type="button"
                    className="arrow arrow-up"
                    onClick={this.onClickArrowUp}
                    ref={(ref) => { this.refArrowUp = ref; }}
                />
                <button
                    type="button"
                    className="arrow arrow-down"
                    onClick={this.onClickArrowDown}
                    ref={(ref) => { this.refArrowDown = ref; }}
                />
            </div>
        );
    }
}

HomePage.propTypes = propTypes;
HomePage.defaultProps = defaultProps;

const mapStateToProps = state => ({
    statsCount: state.count.count,
    statsCountries: state.count.countries,
});

const enhancer = compose(
    withUrlGenerator(),
    withTranslations(),
    connect(mapStateToProps),
);
export default enhancer(HomePage);



// WEBPACK FOOTER //
// ./components/pages/Home.jsx